import React from 'react';

import footerContent from 'platform/forms/components/FormFooter';
import { VA_FORM_IDS } from 'platform/forms/constants';
import {
  profilePersonalInfoPage,
  profileContactInfoPages,
} from 'platform/forms-system/src/js/patterns/prefill';
import { getContent } from 'platform/forms-system/src/js/utilities/data/profile';

import { TITLE, SUBTITLE, SUBMIT_URL } from '../constants';
import manifest from '../manifest.json';
import prefillTransform from './prefillTransform';
import submitForm from './submitForm';
import transform from './transform';

// Components
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';

import CustomReviewTopContent from '../components/CustomReviewTopContent';
import PreSubmitInfo from '../components/PreSubmitInfo';

// Pages
import {
  educationBenefitsEligibility,
  educationBenefitsHistory,
  hasPreviouslyApplied,
  payeeNumber,
  testName,
  organizationInfo,
  prepCourseName,
  prepCourseAddress,
  prepCourseOnline,
  prepCoursePeriod,
  prepCourseCost,
  remarks,
  supportingDocuments,
} from '../pages';

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: SUBMIT_URL,
  submit: submitForm,
  trackingPrefix: '10272-edu-benefits',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  dev: {
    showNavLinks: true,
    collapsibleNavLinks: true,
    disableWindowUnloadInCI: true,
  },
  formId: VA_FORM_IDS.FORM_22_10272,
  saveInProgress: {
    messages: {
      inProgress: 'Your request (22-10272) is in progress.',
      expired:
        'Your saved request (22-10272) has expired. Please start a new request.',
      saved: 'Your request has been saved.',
    },
  },
  version: 0,
  prefillEnabled: true,
  prefillTransformer: prefillTransform,
  preSubmitInfo: {
    CustomComponent: PreSubmitInfo,
    field: 'statementOfTruthCertified',
    statementOfTruth: {
      heading: 'Certification statement',
      body: (
        <span className="vads-u-display--block vads-u-margin-bottom--2">
          I confirm that the identifying information in this form is accurate
          and has been represented correctly.
        </span>
      ),
      messageAriaDescribedby: 'I have read and accept the privacy policy.',
      fullNamePath: 'applicantName',
    },
  },
  savedFormMessages: {
    notFound: 'Please start over.',
    noAuth: 'Please sign in again to continue your request.',
  },
  title: TITLE,
  subTitle: SUBTITLE,
  CustomReviewTopContent,
  customText: {
    appType: 'request',
    continueAppButtonText: 'Continue your request',
    startNewAppButtonText: 'Start a new request',
    finishAppLaterMessage: 'Finish this request later',
    appSavedSuccessfullyMessage: 'We’ve saved your request.',
    reviewPageTitle: 'Review',
    submitButtonText: 'Continue',
  },
  defaultDefinitions: {},
  transformForSubmit: transform,
  useCustomScrollAndFocus: true,
  chapters: {
    educationBenefitsChapter: {
      title: 'Your education benefits information',
      pages: {
        hasPreviouslyApplied: {
          path: 'previously-applied',
          title: 'Your VA education benefits',
          uiSchema: hasPreviouslyApplied.uiSchema,
          schema: hasPreviouslyApplied.schema,
          updateFormData: hasPreviouslyApplied.updateFormData,
        },
        educationBenefitsHistory: {
          path: 'education-benefits-history',
          title: 'Your VA education benefits',
          uiSchema: educationBenefitsHistory.uiSchema,
          schema: educationBenefitsHistory.schema,
          depends: formData => formData?.hasPreviouslyApplied === true,
        },
        educationBenefitsEligibility: {
          path: 'education-benefits-eligibility',
          title: 'Your VA education benefits history',
          uiSchema: educationBenefitsEligibility.uiSchema,
          schema: educationBenefitsEligibility.schema,
          depends: formData => formData?.hasPreviouslyApplied === false,
        },
      },
    },
    personalInformationChapter: {
      title: 'Your personal information',
      pages: {
        ...profilePersonalInfoPage({
          personalInfoConfig: {
            name: { show: true, required: true },
            ssn: { show: true, required: true },
            vaFileNumber: { show: true, required: false },
            dateOfBirth: { show: true, required: false },
          },
          dataAdapter: {
            ssnPath: 'ssnLast4',
            vaFileNumberPath: 'vaFileNumberLast4',
          },
          header: (
            <h3 className="vads-u-margin-bottom--3">
              Confirm the personal information we have on file for you
            </h3>
          ),
        }),
        payeeNumber: {
          path: 'payee-number',
          title: 'Your VA payee number',
          uiSchema: payeeNumber.uiSchema,
          schema: payeeNumber.schema,
        },
        ...profileContactInfoPages({
          content: {
            ...getContent('request'),
            title: 'Confirm the contact information we have on file for you',
          },
          contactInfoRequiredKeys: ['mailingAddress'],
          prefillPatternEnabled: false,
        }),
      },
    },
    licensingAndCertificationChapter: {
      title: 'Licensing and certification details',
      pages: {
        testName: {
          path: 'test-name',
          title:
            "Tell us about the licensing or certification test you're preparing for",
          uiSchema: testName.uiSchema,
          schema: testName.schema,
        },
        organizationInfo: {
          path: 'organization-info',
          title:
            'The name and mailing address of organization awarding license or certification',
          uiSchema: organizationInfo.uiSchema,
          schema: organizationInfo.schema,
          initialData: {
            organizationAddress: { country: 'USA' },
          },
        },
      },
    },
    prepCourseChapter: {
      title: 'Prep course details',
      pages: {
        prepCourseName: {
          path: 'prep-course-details',
          title: 'The name of the prep course',
          uiSchema: prepCourseName.uiSchema,
          schema: prepCourseName.schema,
        },
        prepCourseAddress: {
          path: 'prep-course-details-1',
          title:
            'The name and mailing address of the organization giving the prep course',
          uiSchema: prepCourseAddress.uiSchema,
          schema: prepCourseAddress.schema,
          initialData: {
            prepCourseOrganizationAddress: { country: 'USA' },
          },
        },
        prepCourseOnline: {
          path: 'prep-course-details-2',
          title: `How you'll take the prep course`,
          uiSchema: prepCourseOnline.uiSchema,
          schema: prepCourseOnline.schema,
        },
        prepCoursePeriod: {
          path: 'prep-course-details-3',
          title: 'Provide the start and end dates of your prep course',
          uiSchema: prepCoursePeriod.uiSchema,
          schema: prepCoursePeriod.schema,
        },
        prepCourseCost: {
          path: 'prep-course-details-4',
          title:
            'Enter the cost of the prep course including any mandatory fees',
          uiSchema: prepCourseCost.uiSchema,
          schema: prepCourseCost.schema,
        },
      },
    },
    remarksChapter: {
      title: 'Remarks',
      pages: {
        remarks: {
          path: 'remarks',
          title: 'Enter your remarks',
          uiSchema: remarks.uiSchema,
          schema: remarks.schema,
        },
      },
    },
    supportingDocumentsChapter: {
      title: 'Supporting documents upload',
      pages: {
        supportingDocuments: {
          path: 'supporting-documents',
          title: 'Upload your supporting documents',
          uiSchema: supportingDocuments.uiSchema,
          schema: supportingDocuments.schema,
        },
      },
    },
  },
  // getHelp,
  footerContent,
};

export default formConfig;
